// Pure Othello / Reversi engine. No rendering, no platform, no globals.
// BoardState is the single authority: the UI asks for legal moves and
// dispatches a coordinate; it never mutates cells or counts itself.

export type Disc = "dark" | "light";
export type Cell = Disc | null;
export type Board = Cell[]; // length 64, index = row * 8 + col

export const SIZE = 8;
export const DARK: Disc = "dark"; // moves first
export const LIGHT: Disc = "light";

const DIRECTIONS = [
  [-1, -1], [-1, 0], [-1, 1],
  [0, -1],           [0, 1],
  [1, -1],  [1, 0],  [1, 1],
] as const;

export function opponent(d: Disc): Disc {
  return d === DARK ? LIGHT : DARK;
}

export function idx(row: number, col: number): number {
  return row * SIZE + col;
}

export function createBoard(): Board {
  const b: Board = new Array(64).fill(null);
  b[idx(3, 3)] = LIGHT;
  b[idx(3, 4)] = DARK;
  b[idx(4, 3)] = DARK;
  b[idx(4, 4)] = LIGHT;
  return b;
}

// Cells flipped if `player` places at `move`. Empty array => illegal move.
export function flipsFor(board: Board, move: number, player: Disc): number[] {
  if (board[move] !== null) return [];
  const row = Math.floor(move / SIZE);
  const col = move % SIZE;
  const foe = opponent(player);
  const flips: number[] = [];

  for (const [dr, dc] of DIRECTIONS) {
    let r = row + dr;
    let c = col + dc;
    const line: number[] = [];
    while (r >= 0 && r < SIZE && c >= 0 && c < SIZE && board[idx(r, c)] === foe) {
      line.push(idx(r, c));
      r += dr;
      c += dc;
    }
    if (
      line.length > 0 &&
      r >= 0 && r < SIZE && c >= 0 && c < SIZE &&
      board[idx(r, c)] === player
    ) {
      flips.push(...line);
    }
  }
  return flips;
}

export function legalMoves(board: Board, player: Disc): number[] {
  const moves: number[] = [];
  for (let i = 0; i < 64; i++) {
    if (board[i] === null && flipsFor(board, i, player).length > 0) moves.push(i);
  }
  return moves;
}

export function hasLegalMove(board: Board, player: Disc): boolean {
  for (let i = 0; i < 64; i++) {
    if (board[i] === null && flipsFor(board, i, player).length > 0) return true;
  }
  return false;
}

export interface ApplyResult {
  board: Board;
  flipped: number[];
}

// Returns a NEW board with `move` placed and all bracketed lines converted.
// Throws on an illegal move so bugs surface instead of corrupting state.
export function applyMove(board: Board, move: number, player: Disc): ApplyResult {
  const flipped = flipsFor(board, move, player);
  if (flipped.length === 0) {
    throw new Error(`illegal move ${move} for ${player}`);
  }
  const next = board.slice();
  next[move] = player;
  for (const f of flipped) next[f] = player;
  return { board: next, flipped };
}

export interface Score {
  dark: number;
  light: number;
  empty: number;
}

export function score(board: Board): Score {
  let dark = 0, light = 0, empty = 0;
  for (const c of board) {
    if (c === DARK) dark++;
    else if (c === LIGHT) light++;
    else empty++;
  }
  return { dark, light, empty };
}

export type Outcome = "dark" | "light" | "draw";

export interface EndState {
  ended: boolean;
  outcome?: Outcome;
}

// The match ends when neither player has a legal move (full board or a
// double pass). Majority of discs wins; equal counts draw.
export function checkEnd(board: Board): EndState {
  if (hasLegalMove(board, DARK) || hasLegalMove(board, LIGHT)) {
    return { ended: false };
  }
  const s = score(board);
  const outcome: Outcome =
    s.dark === s.light ? "draw" : s.dark > s.light ? "dark" : "light";
  return { ended: true, outcome };
}

// Whose turn is next after `justMoved`. Skips a player with no legal move.
// Returns null when the match has ended.
export function nextTurn(board: Board, justMoved: Disc): Disc | null {
  const foe = opponent(justMoved);
  if (hasLegalMove(board, foe)) return foe;
  if (hasLegalMove(board, justMoved)) return justMoved; // opponent passes
  return null; // both stuck -> ended
}
